import React, { useCallback, useEffect, useState } from 'react';
import Input from '../../../components/floating-placeholder-input/input';
import { AutoComplete, DatePicker, Popconfirm } from 'antd';
import moment, { Moment } from 'moment';
import TimePickerWrapper from '../../../components/date-time-picker/time-picker';
import { AutoCompleteAddress, BringgGeocodedAddress, WayPoint } from '@bringg/types';
import classNames from 'classnames';
import ButtonWrapper from '../../../components/button/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import { Controller, useForm } from 'react-hook-form';
import {
	mapValues as _mapValues,
	get as _get,
	forEach as _forEach,
	toNumber as _toNumber,
	omit as _omit
} from 'lodash';
import { Modal, Switch, Tooltip } from '../../../components';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { useDebounce } from '../../../custom-hooks/use-debounce/use-debounce';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons/faExclamationCircle';
import {
	convertDateToMoment,
	convertMomentToSeconds,
	convertSecondsToMoment,
	getDateFieldValue,
	getTimeWindow
} from './utils/waypoint-card-form.utils';
import { DefaultDateFormat } from '../../../types/common.consts';
import { latLngBounds } from './constants/lat-lng-bounds';
import { secondsInTimeFormat } from '../../../services/filters';

const { Option } = AutoComplete;

export interface Translations {
	destination: string;
	addressLineOne: string;
	addressLineTwo: string;
	borough: string;
	city: string;
	zipcode: string;
	latitude: string;
	longitude: string;
	scheduleTo: string;
	date: string;
	time: string;
	startEndDateAndTime: string;
	timeWindow: string;
	etos: string;
	contact: string;
	name: string;
	phone: string;
	email: string;
	confirmDelete: string;
	yes: string;
	no: string;
	asap: string;
	delete: string;
	cancel: string;
	save: string;
	warning: string;
	buttonClear: string;
	clearMessage: string;
	deliveryTime: string;
	inventory_etos_tooltip: string;
}

interface WayPointCardFormProps {
	wayPoint: WayPoint;
	onSave: (wayPoint: WayPoint) => void;
	onCancel: () => void;
	onDelete: (wayPointId: number) => void;
	translations: Translations;
	fetchAddresses: (query) => Promise<AutoCompleteAddress[]>;
	getPlaceDetails: (placeId) => Promise<BringgGeocodedAddress>;
	timeZone: string;
	dateFormat?: string;
	hourFormat: string;
	editBaseEtosAsEtos?: boolean;
}

const GetFormSection: React.FC<{
	title: string;
	inlineSection?: boolean;
	horizontalPadding?: boolean;
	paddingBottom?: boolean;
	gridTemplateAuto?: boolean;
	className?: string;
	paddingTop?: number;
}> = ({
	title,
	inlineSection = true,
	horizontalPadding = true,
	paddingBottom = true,
	gridTemplateAuto = false,
	paddingTop = 25,
	className,
	children
}) => (
	<div
		className={classNames(
			'waypoint-card-form-section',
			{ 'waypoint-card-form-inline-section': inlineSection },
			{ 'horizontal-padding': horizontalPadding },
			{ 'padding-bottom': paddingBottom },
			{ 'grid-template-auto': gridTemplateAuto },
			className
		)}
		section-title={title}
		style={{ paddingTop }}
	>
		{children}
	</div>
);

const WayPointCardForm: React.FC<WayPointCardFormProps> = ({
	wayPoint,
	onSave,
	onCancel,
	onDelete,
	translations,
	fetchAddresses,
	getPlaceDetails,
	timeZone,
	dateFormat = DefaultDateFormat,
	hourFormat,
	editBaseEtosAsEtos = false
}) => {
	const delay = 300;
	const [wayPointData, setWayPointData] = useState({ ...wayPoint });
	const inventoryEtos =
		editBaseEtosAsEtos &&
		wayPointData.base_etos != null &&
		!!wayPointData.etos &&
		wayPointData.etos > wayPointData.base_etos &&
		wayPointData.etos - wayPointData.base_etos;

	const defaultEtos = inventoryEtos
		? convertSecondsToMoment(wayPointData.base_etos)
		: convertSecondsToMoment(wayPointData.etos) || '';

	const defaultValues = {
		address: wayPointData.address || '',
		address_second_line: wayPointData.address_second_line || '',
		borough: wayPointData.borough || '',
		city: wayPointData.city || '',
		zipcode: wayPointData.zipcode || '',
		asap: wayPointData.asap || '',
		name: wayPointData.name || '',
		phone: wayPointData.phone || '',
		email: wayPointData.email || '',
		lat: wayPointData.lat || '',
		lng: wayPointData.lng || '',
		scheduled_at: convertDateToMoment(wayPointData.scheduled_at, timeZone),
		etos: defaultEtos,
		time_window: getTimeWindow(wayPointData.no_earlier_than, wayPointData.no_later_than, timeZone)
	};

	const { control, handleSubmit, setValue, getValues, formState } = useForm({
		mode: 'onChange',
		defaultValues
	});

	const [addressOptions, setAddressOptions] = useState([]);
	const [resetModalOpen, setResetModalOpen] = useState<boolean>(false);
	const [query, setQuery] = useState('');
	const [resetClicked, setResetClicked] = useState<boolean>(false);
	const debouncedQuery = useDebounce(query, delay);

	const onSubmit = useCallback(
		formFields => {
			const NUMBERS_FIELDS = ['lat', 'lng'];

			_forEach(NUMBERS_FIELDS, fieldName => {
				formFields[fieldName] = _toNumber(formFields[fieldName]);
			});

			if (formFields.etos) {
				formFields.etos = convertMomentToSeconds(formFields.etos);
			}

			formFields['time_window'] = getValues('time_window');
			formFields['scheduled_at'] = getValues('scheduled_at');

			if (formFields['time_window'][0] || resetClicked) {
				formFields['no_earlier_than'] = getDateFieldValue(formFields['time_window'][0], timeZone);
			}

			if (formFields['time_window'][1] || resetClicked) {
				formFields['no_later_than'] = getDateFieldValue(formFields['time_window'][1], timeZone);
			}

			formFields = _omit(formFields, 'time_window');

			if (formFields['scheduled_at'] || resetClicked) {
				formFields['scheduled_at'] = getDateFieldValue(formFields['scheduled_at'], timeZone);
			} else {
				formFields = _omit(formFields, 'scheduled_at');
			}

			const editedWayPointData = _mapValues(formFields, field =>
				moment.isMoment(field) ? (field as Moment).toISOString() : field
			);

			setWayPointData(Object.assign(wayPointData, editedWayPointData));
			onSave(wayPointData);
		},
		[wayPointData, resetClicked]
	);

	const onCancelForm = () => {
		onCancel();
	};

	const onAddressSelected = async address => {
		const placeId = addressOptions.find(item => item.address === address).place_id;
		const addressDetails = await getPlaceDetails(placeId);
		setValue('city', addressDetails.city, { shouldDirty: true });
		setValue('zipcode', addressDetails.zipcode, { shouldDirty: true });
		setValue('lat', addressDetails.lat, { shouldDirty: true });
		setValue('lng', addressDetails.lng, { shouldDirty: true });
	};

	useEffect(() => {
		const searchAddresses = async () => {
			if (!debouncedQuery) {
				setAddressOptions([]);
				return;
			}
			const options = await fetchAddresses(debouncedQuery);
			setAddressOptions(options);
		};
		searchAddresses();
	}, [debouncedQuery]);

	const handleKeyDown = event => {
		if (event.keyCode === 27) {
			onCancelForm();
		}
	};

	const resetValues = () => {
		setValue('scheduled_at', '', { shouldDirty: true });
		setValue('time_window', ['', ''], { shouldDirty: true });
		setResetClicked(true);
		setResetModalOpen(false);
	};

	const onLatLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const inputName = e.currentTarget.name;
		const inputValue = e.currentTarget.value;
		const inputValueAsFloat = parseFloat(inputValue);

		if (inputValueAsFloat > latLngBounds[inputName].max) {
			setValue(inputName, latLngBounds[inputName].max, { shouldDirty: true });
			return;
		}

		if (inputValueAsFloat < latLngBounds[inputName].min) {
			setValue(inputName, latLngBounds[inputName].min, { shouldDirty: true });
			return;
		}

		setValue(inputName, inputValue, { shouldDirty: true });
	};

	return (
		<>
			<form className="waypoint-card-form" onKeyDown={handleKeyDown}>
				<GetFormSection title={translations.destination}>
					<div
						className="waypoint-card-form-address-line-one"
						input-placeholder={translations.addressLineOne}
					>
						<Controller
							as={
								<AutoComplete
									className="waypoint-card-autocomplete"
									onSelect={onAddressSelected}
									onSearch={query => setQuery(query)}
								>
									{addressOptions &&
										addressOptions.map(({ address }) => {
											return (
												<Option key={address} value={address}>
													{address}
												</Option>
											);
										})}
								</AutoComplete>
							}
							name={'address'}
							control={control}
						/>
					</div>

					<Controller
						as={
							<Input
								className="waypoint-card-form-input address-second-line"
								placeholder={translations.addressLineTwo}
							/>
						}
						name={'address_second_line'}
						control={control}
					/>

					<Controller
						as={<Input className="waypoint-card-form-input borough" placeholder={translations.borough} />}
						name={'borough'}
						control={control}
					/>

					<Controller
						as={
							<Input
								className="waypoint-card-form-input city"
								placeholder={translations.city}
								value={wayPointData.city || ''}
							/>
						}
						name={'city'}
						control={control}
					/>

					<Controller
						as={<Input className="waypoint-card-form-input zipcode" placeholder={translations.zipcode} />}
						name={'zipcode'}
						control={control}
					/>

					<div className="waypoint-card-form-location-inputs-container">
						<Controller
							render={({ value }) => (
								<Input
									className="waypoint-card-form-input lat"
									type="number"
									placeholder={translations.latitude}
									value={value}
									name="lat"
									onChange={onLatLngChange}
									dataTestId={'lat'}
								/>
							)}
							name={'lat'}
							control={control}
						/>

						<Controller
							render={({ value }) => (
								<Input
									className="waypoint-card-form-input lng"
									value={value}
									type="number"
									placeholder={translations.longitude}
									name="lng"
									onChange={onLatLngChange}
									dataTestId={'lng'}
								/>
							)}
							name={'lng'}
							control={control}
						/>
					</div>
				</GetFormSection>

				<GetFormSection title={translations.deliveryTime} inlineSection={false} paddingTop={40}>
					<div className="container-with-btn-clear">
						<ButtonWrapper className="btn-clear" type="link" onClick={() => setResetModalOpen(true)}>
							{translations.buttonClear}
						</ButtonWrapper>
						<GetFormSection title={translations.scheduleTo} horizontalPadding={false}>
							<>
								<div
									className="waypoint-card-form-date-picker-container"
									input-placeholder={translations.date}
								>
									<Controller
										render={({ value, onChange }) => (
											<DatePicker
												data-test-id="scheduled-at"
												className="waypoint-card-form-picker"
												allowClear={false}
												value={value || ''}
												onChange={value => {
													onChange(value);
												}}
												showTime={{ format: hourFormat }}
												format={`${dateFormat} ${hourFormat}`}
											/>
										)}
										name={'scheduled_at'}
										control={control}
									/>
								</div>

								<div className="waypoint-card-form-asap-container asap">
									<Controller
										render={({ value, onChange }) => (
											<Switch
												checked={value}
												onChange={onChange}
												unCheckedChildren={<FontAwesomeIcon icon={faTimes} />}
												checkedChildren={<FontAwesomeIcon icon={faCheck} />}
											/>
										)}
										name={'asap'}
										control={control}
									/>
									<span>{translations.asap}</span>
								</div>
							</>
						</GetFormSection>

						<GetFormSection
							title={translations.timeWindow}
							horizontalPadding={false}
							paddingBottom={false}
							gridTemplateAuto={true}
						>
							<>
								<div
									className="waypoint-card-form-date-picker-container time-window"
									input-placeholder={translations.startEndDateAndTime}
								>
									<Controller
										render={({ value, onChange }) => (
											<DatePicker.RangePicker
												data-test-id="time-window"
												className="waypoint-card-form-picker"
												allowClear={false}
												allowEmpty={[true, true]}
												value={value || ''}
												onChange={value => {
													onChange(value);
												}}
												showTime={{ format: hourFormat }}
												format={`${dateFormat} ${hourFormat}`}
											/>
										)}
										name={'time_window'}
										control={control}
									/>
								</div>

								<div
									className="waypoint-card-form-date-picker-container etos"
									input-placeholder={translations.etos}
								>
									<Controller
										render={({ value, onChange }) => (
											<TimePickerWrapper
												value={value || ''}
												onChange={onChange}
												use12Hours={false}
												format={'HH:mm:ss'}
												className="waypoint-card-form-picker"
												allowClear={false}
												showNow={false}
											/>
										)}
										name={'etos'}
										control={control}
									/>
									{inventoryEtos && (
										<Tooltip title={translations.inventory_etos_tooltip}>
											<span className="inventory-etos" data-test-id="inventory-etos">
												+ {secondsInTimeFormat(inventoryEtos)}
											</span>
										</Tooltip>
									)}
								</div>
							</>
						</GetFormSection>
					</div>
				</GetFormSection>
				<GetFormSection title={translations.contact}>
					<>
						<Controller
							as={<Input className="waypoint-card-form-input name" placeholder={translations.name} />}
							name={'name'}
							control={control}
						/>
						<Controller
							as={<Input className="waypoint-card-form-input phone" placeholder={translations.phone} />}
							name={'phone'}
							control={control}
						/>
						<Controller
							as={<Input className="waypoint-card-form-input email" placeholder={translations.email} />}
							name={'email'}
							control={control}
						/>
					</>
				</GetFormSection>

				<div className="waypoint-card-form-buttons">
					<div>
						<Popconfirm
							overlayClassName="waypoint-card-form-confirm-delete"
							placement="topLeft"
							title={translations.confirmDelete}
							icon={<FontAwesomeIcon className="anticon" icon={faQuestionCircle} />}
							onConfirm={() => onDelete(wayPointData.id)}
							okText={translations.yes}
							cancelText={translations.no}
						>
							<ButtonWrapper danger type="primary" className="waypoint-card-form-button delete-button">
								{translations.delete}
							</ButtonWrapper>
						</Popconfirm>
					</div>

					<div>
						<ButtonWrapper className="waypoint-card-form-button cancel-button" onClick={onCancelForm}>
							{translations.cancel}
						</ButtonWrapper>
						<ButtonWrapper
							disabled={!formState.isDirty}
							type={'primary'}
							className="waypoint-card-form-button save-button"
							onClick={handleSubmit(onSubmit)}
						>
							{translations.save}
						</ButtonWrapper>
					</div>
				</div>
			</form>
			<Modal
				className="modal-reset-time-window"
				visible={Boolean(resetModalOpen)}
				onCancel={() => setResetModalOpen(false)}
				onOk={resetValues}
				cancelText={translations.cancel}
				okText={translations.yes}
				width={454}
				centered={true}
			>
				<div className="div-title-icon">
					<FontAwesomeIcon icon={faExclamationCircle} className="icon" />
					<div className="title">{translations.warning}</div>
				</div>
				<div className="text">{translations.clearMessage}</div>
			</Modal>
		</>
	);
};

export default WayPointCardForm;
